import { useState } from 'react'
import { Card, Row } from 'react-bootstrap'
import { getCountryRiskColor } from '../../helpers/stammdaten.helper'
import { t } from '../../locale'
import { Client, UserAccess } from '../../react-app-env'
import ClientActionButtons from './ClientActionButtons'

interface ClientCardViewProps {
  clients: Client[]
  selectedClients: Client[]
  userAccess: UserAccess
  clientStatusParam: string | null
  countryRisks: Record<string, number | null>
  countryNames: Record<string, string>
  notedClientIds: Set<string>
  kycCheckEnabled: boolean
  amlSearchEnabled: boolean
  onCheckboxChange: (client: Client) => void
  onCheck: (client: Client) => void
  onAmlCheck: (client: Client) => void
  onView: (client: Client) => void
  onSurveyForm: (clientId: string, isPerson: number) => void
  onDeactivate: (client: Client) => void
  onDelete: (clientNumber: number, clientId: string) => void
  onRiskChange: (clientId: string, risik: string) => void
}

function ClientCardView({
  clients,
  selectedClients,
  userAccess,
  clientStatusParam,
  countryRisks,
  countryNames,
  notedClientIds,
  kycCheckEnabled,
  amlSearchEnabled,
  onCheckboxChange,
  onCheck,
  onAmlCheck,
  onView,
  onSurveyForm,
  onDeactivate,
  onDelete,
  onRiskChange,
}: ClientCardViewProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  if (clients.length === 0) {
    return (
      <Row className='g-3 mb-5'>
        <p>{t('noClientsFound')}</p>
      </Row>
    )
  }

  return (
    <Row className='g-3 mb-5'>
      {clients.map((client, index) => (
        <div className='col-sm-4 mb-3 mb-sm-0 d-flex' key={client._id}>
          <Card
            className={`shadow border-primary w-100 ${hoveredIndex === index ? 'priceCardHover' : ''}`}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <Card.Body className='d-flex flex-column'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <h5
                  className='card-title text-truncate mb-0'
                  style={{ maxWidth: '100%', whiteSpace: 'nowrap' }}
                  title={`${client.firstName ?? ''} ${client.name}`}
                >
                  {(client.firstName ?? '') + ' ' + client.name}
                </h5>
                <input
                  type='checkbox'
                  className='btn-spacing'
                  checked={selectedClients.some(selected => selected._id === client._id)}
                  onChange={() => onCheckboxChange(client)}
                />
              </div>

              <p className='card-text mb-1'>
                <strong>{client.clientNumber}</strong>
              </p>
              {client.country && (
                <p className='card-text mb-1'>
                  <strong>{t('country')}:</strong>{' '}
                  <span className={getCountryRiskColor(client.country, countryRisks)}>
                    {countryNames[client.country] || client.country}
                  </span>
                </p>
              )}
              <span
                style={{ maxWidth: '170px' }}
                className={`last-check rounded ${client.lastCheck ? (client.risk ? 'border border-danger text-danger' : 'border border-success text-success') : ''}`}
              >
                {client.lastCheck || '\u00A0'}
              </span>

              <select
                className={`mt-2 form-select form-select-sm ${
                  client.risik === 'high'
                    ? 'border border-danger'
                    : client.risik === 'medium'
                      ? 'border border-warning'
                      : client.risik === 'low'
                        ? 'border border-success'
                        : 'border border-secondary'
                }`}
                style={{ maxWidth: '100px' }}
                value={client.risik || 'unknown'}
                onChange={e => onRiskChange(String(client._id), e.target.value)}
              >
                <option value='unknown'>-- </option>
                <option value='low'>{t('risikLow')}</option>
                <option value='medium'>{t('risikMedium')}</option>
                <option value='high'>{t('risikHigh')}</option>
              </select>

              <div className='mt-auto'>
                <ClientActionButtons
                  client={client}
                  userAccess={userAccess}
                  clientStatusParam={clientStatusParam}
                  hasNotes={notedClientIds.has(client._id)}
                  layout='card'
                  kycCheckEnabled={kycCheckEnabled}
                  amlSearchEnabled={amlSearchEnabled}
                  onCheck={onCheck}
                  onAmlCheck={onAmlCheck}
                  onView={onView}
                  onSurveyForm={onSurveyForm}
                  onDeactivate={onDeactivate}
                  onDelete={onDelete}
                />
              </div>
            </Card.Body>
          </Card>
        </div>
      ))}
    </Row>
  )
}

export default ClientCardView
